import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, ChevronDown, X } from "react-feather";
import { useSession } from "../store/session";
import { useCostSheet } from "../store/costSheet";
import { getTaskCost, type CostEntry, type CostLine, type TaskCost } from "../store/api";
import { usePollWhileVisible } from "../lib/usePollWhileVisible";
import { formatSgd, formatTokens, costRows, tokenTotal, taskSpan } from "../lib/costPresentation";
import "../styles/helpers.css";
import "../styles/cost.css";
import { HelperBot } from "./HelperFaces";

/**
 * ⋯ → Cost breakdown (28 Sep 2026): a receipt for the chat's latest task — the main
 * agent and every helper it started, each with its tokens (input, output, cache
 * write, cache read) and cost at Opus 5.5 API rates, in SGD (USD × 1.3). Same sheet
 * as Helpers: bottom sheet on a phone, centred dialog with a mouse. Nothing is fetched
 * unless it is open; it refreshes every 8 s while open so a running task keeps counting.
 */
const POLL_MS = 8000;

export default function CostReceipt() {
  const companyId = useCostSheet((s) => s.companyId);
  const at = useCostSheet((s) => s.at);
  const activeCompanyId = useSession((s) => s.activeCompanyId);
  useEffect(() => {
    if (companyId && companyId !== activeCompanyId) useCostSheet.getState().close();
  }, [companyId, activeCompanyId]);
  if (!companyId || companyId !== activeCompanyId) return null;
  return createPortal(<CostDialog key={`${companyId}:${at ?? "latest"}`} companyId={companyId} at={at} />, document.body);
}

function CostDialog({ companyId, at }: { companyId: string; at: number | null }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [cost, setCost] = useState<TaskCost | null>(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const close = useCallback(() => useCostSheet.getState().close(), []);

  useEffect(() => {
    const panel = dialog.current;
    if (!panel) return;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (!panel.open) panel.showModal();
    // Focus the sheet, not the ✕ (no focus ring on a button nobody pressed).
    panel.querySelector<HTMLElement>(".helpersSheet__panel")?.focus({ preventScroll: true });
    return () => { if (panel.open) panel.close(); document.body.style.overflow = overflow; };
  }, []);

  const seq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const body = await getTaskCost(companyId, at);
      if (mine !== seq.current) return;
      setCost(body); setError(false);
    } catch {
      if (mine === seq.current) setError(true);
    }
  }, [companyId, at]);
  usePollWhileVisible(load, POLL_MS, true);

  const rate = cost?.usdToSgd ?? 1.3;
  const total = cost?.total;
  const totalLabel = cost?.partial ? "Recorded subtotal" : "Total";
  const span = cost ? taskSpan(cost.startedAt, cost.lastAt) : "";
  const unpriced = cost ? [...new Set(cost.entries.flatMap((e) => e.cost.unpricedModels))] : [];
  // The main agent wears a bot too: the first face none of this task's helpers has.
  const used = new Set(cost?.entries.map((e) => e.bot).filter((b): b is number => typeof b === "number") ?? []);
  let mainBot = 0;
  while (used.has(mainBot)) mainBot++;

  return (
    <dialog
      ref={dialog}
      className="helpersSheet costSheet"
      aria-labelledby="costSheet-title"
      onCancel={(event) => { event.preventDefault(); close(); }}
      onKeyDown={(event) => event.stopPropagation()}
      onClick={(event) => { if (event.target === event.currentTarget) close(); }}
    >
      <div className="helpersSheet__panel" tabIndex={-1}>
        <div className="helpersSheet__grabber" aria-hidden="true" />
        <div className="helpersSheet__view">
          <header className="helpersSheet__head">
            <div className="helpersSheet__titles">
              <h2 id="costSheet-title">Cost Breakdown</h2>
              <p>{span || "This reply’s task"}</p>
            </div>
            <button type="button" className="helpersSheet__icon" aria-label="Close Cost Breakdown" onClick={close}>
              <X width={20} height={20} aria-hidden="true" />
            </button>
          </header>
          <div className="helpersSheet__body">
            {error && (
              <div className="helpersSheet__banner" role="alert">
                <span>Can’t load the cost — try again.</span>
                <button type="button" onClick={() => void load()}>Retry</button>
              </div>
            )}
            {!cost && !error && (
              <div className="helpersSheet__skeleton" aria-label="Loading cost" role="status">
                {[0, 1, 2].map((i) => <div key={i} className="helpersSheet__skelRow" />)}
              </div>
            )}
            {cost && !cost.available && <p className="helpersSheet__empty">{cost.partial
              ? "Usage could not be verified for this task. No complete cost is available."
              : "No cost details for this reply."}</p>}
            {cost?.available && total && (
              <>
                {cost.prompt && <p className="costSheet__prompt">“{cost.prompt}”</p>}
                {[
                  { key: "main", title: "Main agent", rows: cost.entries.filter((e) => e.kind === "main") },
                  { key: "helpers", title: "Helpers", rows: cost.entries.filter((e) => e.kind !== "main") },
                ].filter((g) => g.rows.length > 0).map((g) => (
                  <section key={g.key} className="helpersGroup costSheet__agents">
                    <h3 className="helpersGroup__label">
                      <span className="helpersGroup__title">{g.title}</span>
                      {g.key === "helpers" && <span className="helpersGroup__summary">· {g.rows.length}</span>}
                    </h3>
                    <ul className="helpersGroup__rows">
                      {g.rows.map((e) => (
                        <li key={`${e.run ?? ""}/${e.id}`}>
                          <CostRow entry={e} rate={rate} mainBot={mainBot}
                            expanded={open === `${e.run ?? ""}/${e.id}`}
                            onToggle={() => setOpen((cur) => (cur === `${e.run ?? ""}/${e.id}` ? null : `${e.run ?? ""}/${e.id}`))} />
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
                <section className="costSheet__total" aria-label={totalLabel}>
                  <h3 className="helpersGroup__label"><span className="helpersGroup__title">{totalLabel}</span></h3>
                  <table className="costRow__table costSheet__totalTable">
                    <thead className="costRow__srOnly"><tr><th>Type</th><th>Tokens</th><th>Cost</th></tr></thead>
                    <tbody>
                      {costRows(total).map((r) => (
                        <tr key={r.key}><th scope="row">{r.label}</th><td>{r.tokens}</td><td>{formatSgd(r.usd, rate)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="costSheet__grand">
                    <span className="costSheet__grandLabel">
                      {totalLabel}
                      <span className="costSheet__grandMeta">
                        {formatTokens(tokenTotal(total))} tokens · {total.calls} {total.calls === 1 ? "call" : "calls"}
                        {cost.entries.length > 1 ? ` · ${cost.entries.length} agents` : ""}
                      </span>
                    </span>
                    <span className="costSheet__grandAmount">{formatSgd(total.usd.total, rate)}</span>
                  </div>
                </section>
                {cost.partial && (
                  <p className="costSheet__warn" role="note">
                    <AlertTriangle width={14} height={14} aria-hidden="true" />
                    <span>Some usage could not be verified. This subtotal is not the complete task cost.</span>
                  </p>
                )}
                {unpriced.length > 0 && (
                  <p className="costSheet__warn" role="note">
                    <AlertTriangle width={14} height={14} aria-hidden="true" />
                    <span>Some calls used {unpriced.join(", ")}. They’re counted in tokens but not priced.</span>
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </dialog>
  );
}

function CostRow({ entry: e, rate, mainBot, expanded, onToggle }: {
  entry: CostEntry; rate: number; mainBot: number; expanded: boolean; onToggle(): void;
}) {
  const c: CostLine = e.cost;
  const detailId = useId();
  return (
    <div className={expanded ? "costRow is-open" : "costRow"}>
      <button type="button" className="helperRow costRow__head" aria-expanded={expanded} aria-controls={expanded ? detailId : undefined} onClick={onToggle}>
        <HelperBot bot={e.kind === "main" ? mainBot : e.bot} id={e.id} state={e.state === "running" ? "running" : "done"} size={40} />
        <span className="helperRow__main">
          <span className="helperRow__line1">
            <span className="helperRow__name">{e.name}</span>
            <span className="costRow__amount">{formatSgd(c.usd.total, rate)}</span>
          </span>
          <span className="costRow__facts">
            {formatTokens(tokenTotal(c))} tokens · {formatTokens(c.output)} out
          </span>
        </span>
        <ChevronDown width={16} height={16} aria-hidden="true" className="costRow__chevron" />
      </button>
      {expanded && (
        <table id={detailId} className="costRow__table">
          <thead className="costRow__srOnly"><tr><th>Type</th><th>Tokens</th><th>Cost</th></tr></thead>
          <tbody>
            {costRows(c).map((r) => (
              <tr key={r.key}>
                <th scope="row">{r.label}</th>
                <td>{r.tokens}</td>
                <td>{formatSgd(r.usd, rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
